/**
 * Helpers de exibição da cobrança de um agendamento — Fase 3 Cobranças.
 *
 * Centralizado aqui pra evitar duplicação entre a Agenda (lista do dia,
 * card compacto) e o PatientAppointmentsTab (histórico do paciente).
 * Não traz lógica de persistência — só formatação e cor.
 */

import type { Appointment } from '@/types/models';

export type PaymentStatus = NonNullable<Appointment['paymentStatus']>;

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  PENDENTE: 'Pendente',
  PAGO: 'Pago',
  PARCIAL: 'Parcial',
  ISENTO: 'Isento',
  CONVENIO: 'Convênio',
  RETORNO: 'Retorno',
};

/** Nada é cobrado do paciente: cortesia (ISENTO), retorno (RETORNO) ou o plano paga depois
 *  (CONVENIO). Os dois gravam `chargedAmount = 0` e ficam fora dos totais. */
export function isZeroCharge(s: PaymentStatus | undefined): boolean {
  return s === 'ISENTO' || s === 'CONVENIO' || s === 'RETORNO';
}

/** Classes Tailwind por status de pagamento — usado em badges. */
export const PAYMENT_STATUS_TONE: Record<PaymentStatus, string> = {
  PENDENTE: 'bg-warning-container/40 text-warning',
  PAGO: 'bg-success-container/40 text-success',
  PARCIAL: 'bg-primary-container/40 text-primary',
  ISENTO: 'bg-surface-container-high text-on-surface-variant',
  CONVENIO: 'bg-tertiary-container/40 text-tertiary',
  RETORNO: 'bg-surface-container-high text-on-surface-variant',
};

/** Cor de ponto/dot pra cards compactos da agenda. */
export const PAYMENT_STATUS_DOT: Record<PaymentStatus, string> = {
  PENDENTE: 'bg-warning',
  PAGO: 'bg-success',
  PARCIAL: 'bg-primary',
  ISENTO: 'bg-on-surface-variant',
  CONVENIO: 'bg-tertiary',
  RETORNO: 'bg-on-surface-variant',
};

export function fmtMoneyBRL(value: number | undefined | null): string {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Converte o texto digitado num input de moeda ("1.234,56", "350,00")
 * em número. Aceita o formato BR (ponto de milhar, vírgula decimal).
 * Retorna undefined quando vazio ou inválido. Espelho do `fmtMoneyInput`.
 */
export function parseMoneyInput(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/** Formata um número pro input de moeda ("350" → "350,00"). Vazio se indefinido. */
export function fmtMoneyInput(value: number | undefined): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return '';
  return value.toFixed(2).replace('.', ',');
}

/**
 * Há cobrança registrada (valor lançado OU isento explícito)?
 * `chargedAt` é nosso flag canônico — só vira truthy depois que o
 * staff clica em "Salvar cobrança" no modal.
 */
export function hasChargeRecord(a: Appointment): boolean {
  return Boolean(a.chargedAt);
}

/**
 * Valor a exibir na agenda sob a política "só o valor já lançado":
 * retorna o `chargedAmount` quando há cobrança com dinheiro real
 * (lançada, não isenta e > 0); senão null (sem lançamento ou isento).
 * Card e rodapé usam isto pra mostrar/somar exatamente o mesmo número.
 * (`hasCashValue` é hoisted — definido mais abaixo no módulo.)
 */
export function displayChargedAmount(a: Appointment): number | null {
  return hasCashValue(a) ? (a.chargedAmount as number) : null;
}

/**
 * Soma dos valores cobrados exibíveis de uma lista (mesma regra de
 * `displayChargedAmount`) — usada no rodapé "total cobrado do dia" pra
 * fechar exatamente com a soma dos valores mostrados linha a linha.
 */
export function sumChargedAmounts(appointments: Appointment[]): number {
  return appointments.reduce(
    (acc, a) => acc + (displayChargedAmount(a) ?? 0),
    0,
  );
}

// ─── Procedimentos (detalhamento multi-item) ──────────────────────
//
// Quando o atendimento tem vários itens faturáveis (Consulta +
// Topografia + Paquimetria…), `Appointment.procedures` guarda o
// detalhamento. `chargedAmount` continua sendo a SOMA (fonte de verdade
// dos totais) — estes helpers só leem o detalhamento pra exibição.

/** Tem detalhamento de procedimentos lançado? */
export function hasProcedureDetail(a: Appointment): boolean {
  return (a.procedures?.length ?? 0) > 0;
}

/** Tem 2+ procedimentos (vale destacar a lista no card/impressão)? */
export function hasMultipleProcedures(a: Appointment): boolean {
  return (a.procedures?.length ?? 0) >= 2;
}

/** Nomes dos procedimentos, na ordem lançada (vazio se sem detalhamento). */
export function procedureNames(a: Appointment): string[] {
  return (a.procedures ?? []).map((p) => p.name).filter(Boolean);
}

/**
 * Resumo curto dos procedimentos pra exibir no card/lista da agenda —
 * ex.: "Topografia + Paquimetria". Limita a `max` nomes e indica o
 * excedente ("+2"). Retorna null quando não há detalhamento.
 */
export function procedureSummary(a: Appointment, max = 3): string | null {
  const names = procedureNames(a);
  if (names.length === 0) return null;
  if (names.length <= max) return names.join(' + ');
  return `${names.slice(0, max).join(' + ')} +${names.length - max}`;
}

// ─── Adiantamentos / saldo (sinal de cirurgia) ────────────────────
//
// `chargedAmount` é o total cobrado; `amountPaid` é o que já entrou
// (sinal/entrada/parcelas). O saldo a receber é a diferença. Para PAGO
// o recebido é o total (implícito); PENDENTE/ISENTO recebem zero.

/** Valor já recebido do paciente neste atendimento. */
export function amountReceived(a: Appointment): number {
  if (a.paymentStatus === 'PAGO') return a.chargedAmount ?? 0;
  if (isZeroCharge(a.paymentStatus)) return 0;
  // PARCIAL (ou qualquer outro) → o que foi explicitamente registrado.
  return typeof a.amountPaid === 'number' ? a.amountPaid : 0;
}

/** Saldo a receber: total cobrado menos o que já entrou (nunca negativo). */
export function remainingBalance(a: Appointment): number {
  const total = a.chargedAmount ?? 0;
  return Math.max(0, total - amountReceived(a));
}

/** Há saldo em aberto relevante? (cobrança lançada, não isenta, saldo > 0
 *  e algum adiantamento registrado — i.e. um pagamento parcial real). */
export function hasOutstandingBalance(a: Appointment): boolean {
  return (
    a.paymentStatus === 'PARCIAL' &&
    (a.chargedAmount ?? 0) > 0 &&
    remainingBalance(a) > 0.001
  );
}

// ─── Aggregation (Fase 4 — Dashboard) ─────────────────────────────
//
// Helpers usados pelo FinancialDashboardPage pra somar `chargedAmount`
// dos agendamentos por período / tipo / convênio. Evita poluir o
// componente de página com lógica de aggregação repetida.
//
// Convenções:
//   - ISENTO conta como cobrança lançada mas NÃO entra em totais
//     financeiros (chargedAmount=0 por design)
//   - PAGO = receita reconhecida
//   - PENDENTE / PARCIAL = a receber
//   - Filtros temporais usam `chargedAt` (não `appointment.date`) —
//     o que importa pro mês fiscal é quando o staff lançou a cobrança

/** Predicado: "essa cobrança vale dinheiro real" (exclui ISENTO e
 *  cobranças sem valor). Usado pra zero-suppress nos breakdowns. */
function hasCashValue(a: Appointment): boolean {
  return (
    Boolean(a.chargedAt) &&
    !isZeroCharge(a.paymentStatus) &&
    typeof a.chargedAmount === 'number' &&
    a.chargedAmount > 0
  );
}

function isPaidStatus(s: PaymentStatus | undefined): boolean {
  return s === 'PAGO';
}

function isReceivableStatus(s: PaymentStatus | undefined): boolean {
  return s === 'PENDENTE' || s === 'PARCIAL';
}

export interface ChargeAggregates {
  /** Soma cobrada e marcada como PAGO no período. */
  totalPaid: number;
  /** Soma cobrada e ainda PENDENTE ou PARCIAL no período. */
  totalReceivable: number;
  /** Soma com status ISENTO (cortesia) — pra contagem; valor é 0. */
  isentoCount: number;
  /** Quantos agendamentos tiveram cobrança lançada no período. */
  chargedCount: number;
  /** Top breakdown por `Appointment.type` (ordenado desc). */
  byType: Array<{ key: string; label: string; amount: number }>;
  /** Top breakdown por convênio (insurerId ou "Particular"). */
  byInsurer: Array<{ key: string; label: string; amount: number }>;
}

export const APPOINTMENT_TYPE_LABEL: Record<Appointment['type'], string> = {
  CONSULTA: 'Consulta',
  RETORNO: 'Retorno',
  PRIMEIRA_VEZ: 'Primeira vez',
  EXAME: 'Exame',
  TELEMEDICINA: 'Telemedicina',
  PROCEDIMENTO: 'Procedimento',
};

/**
 * Agrega cobranças de uma lista de agendamentos dentro de uma janela
 * temporal definida por `chargedAt ∈ [start, end)`. `insurerNameMap`
 * resolve `insurerId → name` (carregado de `clinics/{c}/insurers`);
 * ids sem entrada caem como "Convênio (id curto)".
 */
export function aggregateCharges(
  appointments: Appointment[],
  startMs: number,
  endMs: number,
  insurerNameMap: Record<string, string>,
): ChargeAggregates {
  const inWindow = appointments.filter(
    (a) =>
      a.chargedAt !== undefined &&
      a.chargedAt >= startMs &&
      a.chargedAt < endMs,
  );

  let totalPaid = 0;
  let totalReceivable = 0;
  let isentoCount = 0;
  const typeMap = new Map<string, number>();
  const insurerMap = new Map<string, { label: string; amount: number }>();

  for (const a of inWindow) {
    if (a.paymentStatus === 'ISENTO') isentoCount++;
    if (!hasCashValue(a)) continue;

    const amt = a.chargedAmount as number;

    if (isPaidStatus(a.paymentStatus)) totalPaid += amt;
    if (isReceivableStatus(a.paymentStatus)) totalReceivable += amt;

    typeMap.set(a.type, (typeMap.get(a.type) ?? 0) + amt);

    const insurerKey = a.insurerId ?? '__particular__';
    const insurerLabel = a.insurerId
      ? insurerNameMap[a.insurerId] ?? `Convênio ${a.insurerId.slice(0, 6)}`
      : 'Particular';
    const prev = insurerMap.get(insurerKey);
    insurerMap.set(insurerKey, {
      label: insurerLabel,
      amount: (prev?.amount ?? 0) + amt,
    });
  }

  const byType = [...typeMap.entries()]
    .map(([key, amount]) => ({
      key,
      label: APPOINTMENT_TYPE_LABEL[key as Appointment['type']] ?? key,
      amount,
    }))
    .sort((a, b) => b.amount - a.amount);

  const byInsurer = [...insurerMap.entries()]
    .map(([key, { label, amount }]) => ({ key, label, amount }))
    .sort((a, b) => b.amount - a.amount);

  return {
    totalPaid,
    totalReceivable,
    isentoCount,
    chargedCount: inWindow.length,
    byType,
    byInsurer,
  };
}

/** Conveniência: janela [primeiro dia do mês, primeiro dia do próximo). */
export function monthWindowMs(year: number, monthIdx: number): { startMs: number; endMs: number } {
  return {
    startMs: new Date(year, monthIdx, 1, 0, 0, 0, 0).getTime(),
    endMs: new Date(year, monthIdx + 1, 1, 0, 0, 0, 0).getTime(),
  };
}
